import { motion } from "framer-motion";
import { Brain, Heart, Users, Compass, Unlock, TrendingUp, HelpCircle, Shield, Target, Eye, Zap, Layers, BookOpen, Video, Mic, Calendar, MessageCircle } from "lucide-react";
import { SiInstagram, SiYoutube, SiWhatsapp } from "react-icons/si";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { specialties, formations, methodologies, projects, books, CONTACT_INFO } from "@/lib/index";
import { IMAGES } from "@/assets/images";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  brain: Brain,
  heart: Heart,
  users: Users,
  compass: Compass,
  unlock: Unlock,
  "trending-up": TrendingUp,
  "help-circle": HelpCircle,
  shield: Shield,
  target: Target,
  eye: Eye,
  zap: Zap,
  layers: Layers
};

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { type: "spring", stiffness: 300, damping: 35 }
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.1 } },
  viewport: { once: true, margin: "-100px" }
};

const staggerItem = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { type: "spring", stiffness: 400, damping: 30 }
};

export default function Home() {
  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    const element = document.querySelector(targetId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - headerOffset,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen">
      <section id="inicio" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.HEALING_4}
            alt="Transformação emocional"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>
        
        <motion.div
          className="relative z-10 container mx-auto px-4 text-center"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 40, delay: 0.2 }}
        >
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            Sua transformação começa no sentir.
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground mb-12 max-w-3xl mx-auto font-light">
            Psicanálise e Psicoterapia Integrativa para quem deseja compreender suas emoções, transformar sua história e construir uma vida com mais sentido.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="text-lg px-8 py-6 bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.97]"
              asChild
            >
              <a href="#agendamento" onClick={(e) => handleAnchorClick(e, '#agendamento')}>Agendar sessão</a>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6 border-2 hover:bg-secondary/50 transition-all duration-200 hover:scale-[1.02] active:scale-[0.97]"
              asChild
            >
              <a href="#quem-sou" onClick={(e) => handleAnchorClick(e, '#quem-sou')}>Conhecer meu trabalho</a>
            </Button>
          </div>
        </motion.div>
      </section>

      <section id="quando-procurar" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Quando procurar terapia</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Muitas pessoas procuram terapia quando percebem que algo dentro delas precisa ser compreendido.
            </p>
          </motion.div>

          <motion.div className="max-w-4xl mx-auto mb-20" {...fadeInUp}>
            <Card className="border-2 shadow-lg">
              <CardContent className="p-8">
                <p className="text-lg mb-6 text-muted-foreground">Talvez você esteja:</p>
                <ul className="space-y-4 text-lg">
                  <li className="flex items-start gap-3">
                    <span className="text-accent mt-1">•</span>
                    <span>lidando com ansiedade constante</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-accent mt-1">•</span>
                    <span>enfrentando conflitos em relacionamentos</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-accent mt-1">•</span>
                    <span>carregando dores emocionais do passado</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-accent mt-1">•</span>
                    <span>sentindo vazio ou falta de propósito</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-accent mt-1">•</span>
                    <span>vivendo crises emocionais</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-accent mt-1">•</span>
                    <span>desejando se conhecer profundamente</span>
                  </li>
                </ul>
                <p className="text-lg mt-8 text-muted-foreground">
                  A psicoterapia é um espaço seguro para compreender emoções, reorganizar pensamentos e construir novos caminhos.
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <div>
            <motion.h3 className="text-3xl font-bold text-center mb-12" {...fadeInUp}>
              Especialidades
            </motion.h3>
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              variants={staggerContainer}
              initial="initial"
              whileInView="whileInView"
              viewport={{ once: true, margin: "-100px" }}
            >
              {specialties.map((specialty) => {
                const IconComponent = iconMap[specialty.icon];
                return (
                  <motion.div key={specialty.id} variants={staggerItem}>
                    <Card className="h-full hover:shadow-xl transition-all duration-200 hover:scale-[1.02] border-2">
                      <CardHeader>
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                          {IconComponent && <IconComponent className="w-6 h-6 text-primary" />}
                        </div>
                        <CardTitle className="text-xl">{specialty.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-base">{specialty.description}</CardDescription>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>

      <section id="quem-sou" className="py-24">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Quem é Erick Torritezi</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <motion.div {...fadeInUp}>
              <div className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={IMAGES.ERICK_300X300_40}
                  alt="Erick Torritezi - Psicanalista e Psicoterapeuta"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div className="space-y-6" {...fadeInUp}>
              <p className="text-lg leading-relaxed">
                Erick Torritezi é <strong>Psicanalista, Psicoterapeuta Integrativo</strong> e especialista em desenvolvimento humano.
              </p>
              <p className="text-lg leading-relaxed text-muted-foreground">
                Com mais de <strong>15 anos de experiência</strong> trabalhando com comportamento humano, Erick dedica sua vida a ajudar pessoas a compreenderem suas emoções, ressignificarem suas histórias e encontrarem novos caminhos para viver com mais consciência e plenitude.
              </p>
              <p className="text-lg leading-relaxed text-muted-foreground">
                Sua trajetória inclui experiências em empresas nacionais e multinacionais, atuando com liderança, gestão de pessoas e desenvolvimento humano. Essa vivência ampliou sua compreensão sobre os desafios emocionais enfrentados no mundo contemporâneo.
              </p>
              <p className="text-lg leading-relaxed text-muted-foreground">
                Ao longo de sua jornada, aprofundou sua formação em diversas abordagens terapêuticas, integrando <strong>ciência psicológica, espiritualidade e neurociência</strong> em um método terapêutico profundo e transformador.
              </p>
              <p className="text-lg leading-relaxed text-muted-foreground">
                Seu trabalho tem como objetivo ajudar cada pessoa a resgatar aquilo que muitas vezes se perde ao longo da vida: <strong>a própria essência</strong>.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="formacoes" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Formações e Especializações</h2>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
          >
            {formations.map((formation) => (
              <motion.div key={formation.id} variants={staggerItem}>
                <Card className="h-full hover:shadow-xl transition-all duration-200 hover:scale-[1.02] border-2">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                      <BookOpen className="w-6 h-6 text-accent" />
                    </div>
                    <CardTitle className="text-lg">{formation.title}</CardTitle>
                    {formation.institution && (
                      <CardDescription className="text-sm font-medium">{formation.institution}</CardDescription>
                    )}
                  </CardHeader>
                  {formation.description && (
                    <CardContent>
                      <CardDescription>{formation.description}</CardDescription>
                    </CardContent>
                  )}
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="metodologia" className="py-24">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Metodologia de Trabalho</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              A abordagem terapêutica de Erick Torritezi integra diferentes linhas da psicoterapia moderna.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
          >
            {methodologies.map((methodology) => {
              const IconComponent = iconMap[methodology.icon];
              return (
                <motion.div key={methodology.id} variants={staggerItem}>
                  <Card className="h-full hover:shadow-xl transition-all duration-200 hover:scale-[1.02] border-2">
                    <CardHeader>
                      <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                        {IconComponent && <IconComponent className="w-7 h-7 text-primary" />}
                      </div>
                      <CardTitle className="text-xl">{methodology.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base leading-relaxed">{methodology.description}</CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section id="projetos" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Projetos</h2>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
          >
            {projects.map((project) => (
              <motion.div key={project.id} variants={staggerItem}>
                <Card className="h-full hover:shadow-xl transition-all duration-200 hover:scale-[1.02] border-2">
                  <CardHeader>
                    <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                      {project.type === 'video' && <Video className="w-7 h-7 text-accent" />}
                      {project.type === 'podcast' && <Mic className="w-7 h-7 text-accent" />}
                      {project.type === 'content' && <BookOpen className="w-7 h-7 text-accent" />}
                    </div>
                    <CardTitle className="text-2xl">{project.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base leading-relaxed">{project.description}</CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="livros" className="py-24">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Livros</h2>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
          >
            {books.map((book, index) => {
              const bookImages = [IMAGES.VIVER1_42, IMAGES.VIVER2_43, IMAGES.DIALOGO_44];
              return (
                <motion.div key={book.id} variants={staggerItem}>
                  <Card className="h-full hover:shadow-xl transition-all duration-200 hover:scale-[1.02] border-2 overflow-hidden">
                    <div className="aspect-[3/4] relative overflow-hidden">
                      <img
                        src={bookImages[index]}
                        alt={book.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <CardHeader>
                      <CardTitle className="text-xl">{book.title}</CardTitle>
                      {book.edition && (
                        <CardDescription className="text-sm font-medium text-accent">{book.edition}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base leading-relaxed">{book.description}</CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section id="como-funciona" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Como Funciona a Terapia</h2>
          </motion.div>

          <motion.div className="max-w-4xl mx-auto" {...fadeInUp}>
            <Card className="border-2 shadow-lg">
              <CardContent className="p-8 md:p-12">
                <p className="text-lg mb-8 leading-relaxed">
                  As sessões são conduzidas com <strong>acolhimento, confidencialidade e profundidade terapêutica</strong>.
                </p>
                
                <div className="space-y-6 mb-8">
                  <h3 className="text-2xl font-bold mb-4">Modalidades de atendimento:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-primary/5">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Video className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-lg font-medium">Terapia online</span>
                    </div>
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-primary/5">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-lg font-medium">Terapia presencial</span>
                    </div>
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-primary/5">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Brain className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-lg font-medium">Terapia individual</span>
                    </div>
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-primary/5">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Heart className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-lg font-medium">Terapia de casal</span>
                    </div>
                  </div>
                </div>

                <p className="text-lg text-muted-foreground leading-relaxed">
                  Cada processo terapêutico é único e adaptado à realidade de cada pessoa.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      <section id="agendamento" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.MINDFULNESS_5}
            alt="Agende sua sessão"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/90 to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div className="text-center max-w-3xl mx-auto" {...fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Agendamento</h2>
            <p className="text-xl text-muted-foreground mb-12 leading-relaxed">
              Se você sente que chegou o momento de compreender melhor suas emoções e transformar sua história, a terapia pode ser um passo importante.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Button
                size="lg"
                className="text-lg px-8 py-6 bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.97]"
                asChild
              >
                <a href={`https://wa.me/${CONTACT_INFO.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
                  <Calendar className="w-5 h-5 mr-2" />
                  Agendar sessão
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 border-2 hover:bg-secondary/50 transition-all duration-200 hover:scale-[1.02] active:scale-[0.97]"
                asChild
              >
                <a href={`https://wa.me/${CONTACT_INFO.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Conversar pelo WhatsApp
                </a>
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <a
                href={`https://wa.me/${CONTACT_INFO.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-lg hover:text-accent transition-colors"
              >
                <SiWhatsapp className="w-6 h-6" />
                <span>WhatsApp</span>
              </a>
              <a
                href={`https://instagram.com/${CONTACT_INFO.instagram.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-lg hover:text-accent transition-colors"
              >
                <SiInstagram className="w-6 h-6" />
                <span>Instagram</span>
              </a>
              <a
                href={`https://youtube.com/${CONTACT_INFO.youtube.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-lg hover:text-accent transition-colors"
              >
                <SiYoutube className="w-6 h-6" />
                <span>YouTube</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}